// Single source of truth for which redux slices are EXCLUDED from redux-persist.
//
// Kept as a standalone leaf module (no other imports) so the persistence
// contract can be unit-tested without pulling the whole store/reducer tree.
//
// RULE (see the inline notes below): a modal / dialog visibility flag must
// NEVER persist. Each such slice holds only transient UI state
// ({ isOpen, ...labels, ...onClick callbacks }); persisting it writes
// isOpen:true into localStorage["persist:root"], and on the next load
// redux-persist rehydrates it and re-opens a full-screen-backdrop modal that
// intercepts every pointer event — a stuck modal that survives a full reload.
// Persisting also serialises onClick callbacks to null (redux-persist is JSON),
// so a rehydrated popup's buttons are dead.
//
// codeblockStyles (the "carry forward last style" cache read by every
// CodeBlocks/*/index.jsx mount effect, e.g. Image/index.jsx's
// getImageCarryForwardPatch) is deliberately NOT in this list, unlike its
// former neighbors here. It holds only plain style values (numbers/strings/
// small nested objects — margin, borderWidth, borderColor, etc.), never
// callbacks, so it has none of the "dead onClick" or "stuck open" failure
// modes above. Leaving it out of redux-persist meant a full browser reload
// silently reset every block type's "last used style" back to
// CodeblockDefaultStyles.js's hardcoded initialState, so composing a Wheel
// across more than one page load never carried the user's own styling
// forward — measured live: add an Image block, change its margin, reload,
// add a second Image block -> it came back with the hardcoded default
// margin, not the one just set. Persisting this slice is safe because
// EW-1004's LOGOUT handler already resets the ENTIRE store (including this
// slice) to initial state regardless of blacklist membership, so there is no
// shared-device cross-user leak.
export const PERSIST_BLACKLIST = [
  // Builder / editor working state (large, derived, or device-specific).
  "segmentBuilder",
  "wheelWallpaperReducer",
  "gallery",
  "wheelSettings",
  // stgfix-1008-T312-r2-businessbox-card-and-open-editor-race: userProfile
  // (redux/reducers/user.js's USER_PROFILE_SUCCESS) is the own-profile cache
  // that routes.js's app-shell effect and Gallery/index.jsx's own mount
  // effect both treat as "already have it, skip the network fetch" —
  // getOwnProfileIfNeededAction (redux/actions/user.js) and
  // shouldSkipOwnProfileFetch (pages/Gallery/galleryLoadState.js) both only
  // check whether SOME profile for this user id already sits in redux, never
  // whether it is fresh. Left persisted, a FULL page reload rehydrates this
  // slice from localStorage BEFORE either effect runs, so both guards see an
  // "already have it" profile that is actually last session's snapshot and
  // skip the fetch entirely — MEASURED live on web-stg.iyuga.net: a hard
  // reload of a user's own /gallery/:id fired ZERO
  // `GET /api/v1/user/{id}/profile/` requests, and the Business Box card
  // (WheelGallery/Profile/index.jsx's `bboxData`, sourced from this same
  // chain via `gallery.profile`) kept rendering a Business Box with 0 icon
  // links while the server already had 1, with no way to refresh it short of
  // opening the editor (which has its own refetch, stgfix-1008-NEW-BusinessBox-1).
  // Blacklisting only stops the WRITE to storage (and strips any already-
  // persisted copy on the next rehydrate via stripPersistBlacklistedKeys
  // below) — it does not touch the in-memory redux store, so the original
  // firstpaint optimization this guard exists for (skip a SECOND fetch
  // within the SAME page load once the first one already resolved) is fully
  // preserved; only the "satisfied by a stale copy from before this page
  // load even started" case goes away.
  "userProfile",

  // Modal / dialog visibility flags — must never persist (see rule above).
  "galleryPopup",
  "nonAuthenticatedModal",
  // createWheelPopup holds only the transient open/closed state of the
  // Create-a-Wheel wizard. Persisting it means a wizard left open when a tab
  // closes would silently reopen on the next load — and it forced a mount-time
  // hide reset in CreateWheelPopup that raced (and lost to) the ?create=1
  // "open the wizard" handoff.
  "createWheelPopup",
  // Ephemeral dialog-popup slices. Measured live on web-stg: clicking the
  // Wheel-view "Translate" upsell (dispatch openDialogPopup) left the Premium
  // modal stuck across a full page reload until the user found "Maybe later",
  // because dialogPopup was not blacklisted. generalDialog and upgradePopup are
  // the same app-wide dialog class (isOpen + onClick callbacks).
  "dialogPopup",
  "generalDialog",
  "upgradePopup",
  // deleteDialog backs the shared DeleteModal confirm (src/components/DeleteModal)
  // used app-wide, including the Capture "ship to a wheel" delete/detach flows.
  // Same isOpen + onConfirm/onCancel shape as the other modal slices above: left
  // unblacklisted, a delete confirm open at reload time rehydrates stuck open,
  // and its onConfirm/onCancel callbacks are lost to JSON serialization, so the
  // rehydrated dialog's Confirm button silently does nothing.
  "deleteDialog",
  // hotbar.isPreviewOpen gates the Hotbar Images-tab "Preview" overlay
  // (PreviewCarousel, a fixed 100vw/100vh backdrop) the exact same way
  // dialogPopup.isOpen gated the Translate upsell above. Measured live on
  // web-dev: seeding isPreviewOpen:true into persist:root.hotbar then
  // reloading rehydrated the overlay stuck open over the whole builder, with
  // no user action that session.
  "hotbar",
];

// redux-persist's `blacklist` config (passed to persistReducer) only stops a
// PERSIST_BLACKLIST key from being WRITTEN to storage going forward — see
// redux-persist/lib/createPersistoid.js. It does NOT stop that key from being
// READ back in: redux-persist/lib/getStoredState.js deserialises every key
// present in localStorage["persist:root"] unconditionally, and the default
// autoMergeLevel1 stateReconciler merges all of them into the live store on
// REHYDRATE with no blacklist check at all (confirmed by reading both files).
// So any key that predates being added to PERSIST_BLACKLIST — or one an
// attacker/extension/old tab writes directly into localStorage — still
// rehydrates and reopens the stuck modal, defeating the blacklist entirely.
// This helper is wired into persistConfig.migrate (see ../index.js) so those
// keys are dropped from the restored state before it ever reaches the store,
// closing that gap for every slice in PERSIST_BLACKLIST at once.
export const stripPersistBlacklistedKeys = (restoredState) => {
  if (!restoredState || typeof restoredState !== "object") return restoredState;
  const cleaned = { ...restoredState };
  PERSIST_BLACKLIST.forEach((key) => {
    delete cleaned[key];
  });
  return cleaned;
};
